// Site config from GET /api/config, merged over the verified chain facts in SPEC.md so pages still render
// (read-only) if the server is unreachable.
import { api } from "./api.js";

// Coins launch on pump.fun (Solana). The 70/30 split is pump.fun's own creator-fee sharing, fixed at launch.
export const PUMP_PROGRAM = "6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P";

/** Every launch uses the same split (SPEC v1.1): 30% of creator fees to the coin's pool, 70% to the creator. */
export const SHILL_BPS = 3000;

export const DEFAULTS = Object.freeze({
  chain: "solana",
  rpcUrl: "https://api.mainnet-beta.solana.com",
  explorer: "https://solscan.io",
  launchFeeLamports: "0",
  split: { creatorBps: 7000, shillBps: 3000, platformBps: 0 },
  epochMinutes: 30, // payouts run every 30 minutes, counted from each coin's launch
  dailyPostLimit: 3, // counted posts per X account per coin per UTC day
  codePrefix: "sh-",
  aiCheck: false, // true when the server runs the AI check on posts
  harvestMinutes: 15, // creator fees are collected (and the creator's share sent) this often
  xHandle: "",
  coin: null, // optional: the Shilled coin, as a mint address string or { token, symbol }
  curve: null, // optional: { virtualSolLamports, feeBps } enables % presets for the initial buy
  publicUrl: null, // optional: canonical site URL for share links (defaults to location.origin)
});

const posInt = (v, d) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.round(Number(v)) : d);

function merge(c) {
  const src = c && typeof c === "object" ? c : {};
  const out = { ...DEFAULTS, ...src };
  out.split = { ...DEFAULTS.split, ...(src.split || {}) };
  out.explorer = String(out.explorer || DEFAULTS.explorer).replace(/\/+$/, "");
  out.publicUrl = String(out.publicUrl || (typeof location !== "undefined" ? location.origin : "")).replace(/\/+$/, "");
  out.epochMinutes = posInt(out.epochMinutes, DEFAULTS.epochMinutes);
  out.dailyPostLimit = posInt(out.dailyPostLimit, DEFAULTS.dailyPostLimit);
  out.harvestMinutes = posInt(out.harvestMinutes, DEFAULTS.harvestMinutes);
  out.codePrefix = typeof out.codePrefix === "string" && out.codePrefix ? out.codePrefix : DEFAULTS.codePrefix;
  out.aiCheck = out.aiCheck === true;
  return out;
}

let cached = null;
let pending = null;
let loadError = null;

export function loadConfig() {
  if (!pending) {
    pending = api.config().then(
      (c) => (cached = merge(c)),
      (e) => {
        loadError = e;
        cached = merge({});
        return cached;
      },
    );
  }
  return pending;
}

/** The loaded config (or defaults before/without it). */
export const config = () => cached || merge({});
/** The error from loading /api/config, if it failed. */
export const configError = () => loadError;

export const explorerTx = (sig) => `${config().explorer}/tx/${sig}`;
export const explorerAddress = (a) => `${config().explorer}/account/${a}`;
export const explorerToken = (a) => `${config().explorer}/token/${a}`;
export const pumpUrl = (mint) => `https://pump.fun/coin/${mint}`;
/** Solana addresses are base58, 32–44 characters. */
export const isAddress = (a) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(String(a || ""));
export const xProfileUrl = (h) => `https://x.com/${String(h || "").replace(/^@/, "")}`;
export const coinUrl = (token) => `${config().publicUrl}/coin/${token}`;

/** The site's host for copy ("shilledlaunch.com"), from publicUrl. */
export function siteHost(cfg = config()) {
  try { return new URL(cfg.publicUrl).host || cfg.publicUrl; } catch { return String(cfg.publicUrl || "").replace(/^https?:\/\//, ""); }
}

/** "half hour" for 30-minute payouts, "hour" for 60, else "15-minute window". */
export function epochName(cfg = config()) {
  const m = Number(cfg.epochMinutes);
  if (m === 30) return "half hour";
  if (m === 60) return "hour";
  return `${m}-minute window`;
}

/** The Post on X text, exactly Mentioned's shape: `$TICKER <site>/coin/<token> <code>`. */
export const postText = (symbol, token, code) => `$${String(symbol || "").replace(/^\$/, "")} ${coinUrl(token)} ${code}`;
